import * as yup from "yup";
import { Formik, Form as FormikForm } from "formik";
import { Button as BSButton, FloatingLabel, Form } from "react-bootstrap";
import { RefundRequestDTO } from "../../../../data/entities";
import { formatPrice } from "../../../../utils";

export interface RefundFormProps {
  paymentId: number;
  remainingAmount: number;
  refundedAmount: number;
  onSubmit: (refundRequest: RefundRequestDTO) => void;
  submitRef: React.RefObject<HTMLButtonElement>;
}

export function RefundForm({ 
  paymentId, 
  remainingAmount, 
  refundedAmount, 
  onSubmit, 
  submitRef 
}: Readonly<RefundFormProps>) {
  return (
    <Formik
      enableReinitialize
      initialValues={{
        amount: parseFloat(remainingAmount.toFixed(2)),
        reasonOption: "",
        reasonOther: "",
        useOverrideAmount: false,
        confirmAction: false,
      }}
      validationSchema={yup.object().shape({
        amount: yup
          .number()
          .nullable()
          .when("useOverrideAmount", {
            is: true,
            then: (schema) =>
              schema
                .required("Amount is required")
                .min(0.01, "Amount must be greater than 0")
                .max(remainingAmount, `Cannot exceed remaining amount of ${formatPrice(remainingAmount)}`),
            otherwise: (schema) => schema.notRequired(),
          }),
        reasonOption: yup.string().required("Please select a reason"),
        reasonOther: yup
          .string()
          .when("reasonOption", {
            is: "Other",
            then: (schema) => schema.required("Please specify a reason"),
            otherwise: (schema) => schema.notRequired(),
          }),
        confirmAction: yup
          .boolean()
          .oneOf([true], "You must confirm before proceeding."),
      })}
      onSubmit={(values) => {
        onSubmit({
          paymentId: paymentId,
          refundAmount: values.useOverrideAmount ? values.amount : null,
          refundReason: values.reasonOption === "Other" ? values.reasonOther : values.reasonOption,
        });
      }}
    >
      {({ handleSubmit, values, setFieldValue, touched, errors }) => (
        <FormikForm noValidate onSubmit={handleSubmit} className="d-flex flex-column flex-grow-1">
          {/* Reason for refund */}
          <div className="mb-3">
            <Form.Label>Reason for refund</Form.Label>
            <Form.Select
              value={values.reasonOption}
              onChange={(e) => setFieldValue("reasonOption", e.target.value)}
              isInvalid={touched.reasonOption && !!errors.reasonOption}
            >
              <option value="">Select reason</option>
              <option value="Customer Request">Customer Request</option>
              <option value="Service Issue">Service Issue</option>
              <option value="Billing Error">Billing Error</option>
              <option value="Duplicate Charge">Duplicate Charge</option>
              <option value="Other">Other</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.reasonOption}</Form.Control.Feedback>
          </div>

          {values.reasonOption === "Other" && (
            <FloatingLabel label="Specify reason" className="mb-3">
              <Form.Control
                as="textarea"
                type="text"
                value={values.reasonOther}
                style={{ minHeight: "150px" }}
                onChange={(e) => setFieldValue("reasonOther", e.target.value)}
                isInvalid={touched.reasonOther && !!errors.reasonOther}
              />
              <Form.Control.Feedback type="invalid">{errors.reasonOther}</Form.Control.Feedback>
            </FloatingLabel>
          )}

          {/* Override amount for partial refund */}
          <div className="mb-3 mt-2">
            <Form.Check
              type="switch"
              id="override-refund-amount-switch"
              label={refundedAmount > 0 ? "Custom refund amount" : "Partial refund"}
              checked={values.useOverrideAmount}
              onChange={() => setFieldValue("useOverrideAmount", !values.useOverrideAmount)}
            />
            {values.useOverrideAmount && (
              <div className="mt-3">
                <FloatingLabel label={`Refund amount (max ${formatPrice(remainingAmount)})`}>
                  <Form.Control
                    type="number"
                    min={0}
                    max={remainingAmount}
                    step="0.01"
                    value={values.amount ?? ""}
                     onChange={(e) => {
                      const value = e.target.value === "" ? null : parseFloat(parseFloat(e.target.value).toFixed(2));
                      setFieldValue("amount", value);
                    }}
                    isInvalid={touched.amount && !!errors.amount}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.amount}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </div>
            )}
          </div>

          {/* Confirmation checkbox */}
          <div className="mt-auto mb-3 p-3 rounded bg-danger bg-opacity-10 border border-danger">
            <Form.Check
              type="checkbox"
              label={`I confirm refunding ${formatPrice(values.useOverrideAmount ? values.amount ?? 0 : remainingAmount)} to the customer. This action cannot be undone.`}
              checked={values.confirmAction}
              onChange={(e) => setFieldValue("confirmAction", e.target.checked)}
              isInvalid={touched.confirmAction && !!errors.confirmAction}
            />
          </div>

          <BSButton type="submit" className="d-none" ref={submitRef} />
        </FormikForm>
      )}
    </Formik>
  );
}

export default RefundForm;